import styles from '../../EditTable.module.scss';

import clsx from 'clsx';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { Cell, TableInstance } from 'react-table';
import { JsonSchema } from 'rxdb';

import { ColumnAdditionalData } from '@work4all/components';
import { EditModeTableInstance } from '@work4all/components/lib/dataDisplay/basic-table/plugins/useEditMode';

import { RowSizeMode } from '../../../../../../../../../../../../settings/settings';
import { TableCell } from '../../components';
import { genericMemo } from '../../generic-memo';
import { EditTableColumns, EditTableEntry } from '../../types';

interface MemoizedCellProps<T extends EditTableEntry> {
  cell: Cell<T>;
  cellIndex: number;
  row: { original: T };
  definition: EditTableColumns<T>;
  isEditMode: boolean;
  isEditableCell: boolean;
  showSkeleton: boolean;
  isValueVisible: boolean;
  schemaValid: boolean;
  autoFocus: boolean | undefined;
  selectionRange: { rowId: string; cellId: string } | null;
  style: React.CSSProperties;
  otherCellProps: Record<string, unknown>;
  cellClassStyle: React.CSSProperties | undefined;
  rowInEditMode: boolean;
  expanded: boolean;
  toggleExpanded: (value?: boolean) => void;
  cellClick: (e: React.MouseEvent<HTMLElement>) => void;
  stopEdit: () => void;
  onSelectionChange: (start: number, end: number) => void;
  onNext: (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
    dir: 1 | -1
  ) => void;
  getMaxSize: (index: number) => number;
  isRowVisible: (index: number) => boolean;
  rowSizeMode: RowSizeMode;
  tableInstance: TableInstance<T> & EditModeTableInstance;
  editableCols: string[];
  onRowOut?: (row: T, dir: 1 | -1) => void;
  showBorders: boolean;
  tablesLayoutBorders: { value: { vertical: boolean } };
}

function MemoizedCellInternal<T extends EditTableEntry>(
  props: MemoizedCellProps<T>
) {
  const {
    cell,
    row,
    definition,
    isEditMode,
    isEditableCell,
    showSkeleton,
    isValueVisible,
    schemaValid,
    autoFocus,
    selectionRange,
    style,
    otherCellProps,
    cellClassStyle,
    rowInEditMode,
    expanded,
    toggleExpanded,
    cellClick,
    stopEdit,
    onSelectionChange,
    onNext: _onNext,
    getMaxSize,
    isRowVisible,
    rowSizeMode,
    tableInstance,
    editableCols,
    onRowOut,
  } = props;

  const cellType = useMemo(() => {
    let type =
      isEditMode &&
      definition.type !== 'picker' &&
      cell.column.id !== 'selection'
        ? 'EditableCell'
        : 'Cell';

    const cellDefinition =
      'Cell' in definition ? (definition.Cell as { name: string }) : undefined;

    type =
      (!cellDefinition ||
        (cellDefinition && cellDefinition.name === 'EditableCell')) &&
      cell.column.id !== 'expand' &&
      cell.column.id !== 'selection' &&
      type === 'Cell' &&
      definition.type !== 'picker' &&
      definition.type !== 'date' &&
      definition.type !== 'time'
        ? 'EditableCell'
        : type;

    return type;
  }, [isEditMode, definition, cell.column.id]);

  const schema = useMemo(() => getSchema(cell), [cell]);

  const renderedCell = useMemo(() => {
    const render = definition.render;
    const shouldRender = definition.shouldRender;

    if (render && (shouldRender ? shouldRender?.(row.original) : true)) {
      return render(cell, { isEditMode, tableInstance });
    }

    return cell.render(cellType, {
      onEdit: cellClick,
      transform: getTransform(definition, schema),
      ...definition,
      textarea: definition.type === 'text' && definition.resizable !== false,
      disabled: !isValueVisible,
      isValueVisble: isValueVisible,
      maxLength: schema?.maxLength,
      onSubmit: stopEdit,
      onExit: stopEdit,
      onSelectionChange: (start: number, end: number) => {
        if (cell.row.id !== selectionRange?.rowId) {
          tableInstance.setEditModeConfig({
            row: cell.row.id,
            columns: editableCols,
            autoFocus: cell.column.id,
          });
        }
        onSelectionChange(start, end);
      },
      selectinRange: autoFocus ? selectionRange : undefined,
      autoFocus,
      onNext: (
        event: React.KeyboardEvent<HTMLTextAreaElement>,
        dir: 1 | -1
      ) => {
        const sortedCols =
          tableInstance.state.columnOrder.length ===
          tableInstance.visibleColumns.length
            ? tableInstance.state.columnOrder
            : tableInstance.visibleColumns.map((x) => x.id);
        const currentColumns = sortedCols.filter((x) =>
          editableCols.includes(x as string)
        );
        const idx = currentColumns.findIndex((x) => cell.column.id === x);

        const next = currentColumns[idx + dir];

        if (!next) {
          stopEdit();
          onRowOut?.(row.original, dir);
        }
      },
      className: clsx('inside-row', styles.cellSingleLine, {
        [styles['right-oriented']]: definition.type === 'number',
      }),
      maxHeight: getMaxSize(cell.row.index),
      isVisible: isRowVisible(cell.row.index),
      isEditMode: rowInEditMode,
      isEditable: isEditableCell,
      maxLines:
        !definition.multiLinesRowKindsAllowed ||
        definition.multiLinesRowKindsAllowed?.includes(
          cell.row.original.positionKind
        )
          ? undefined
          : 1,
      rowSizeMode,
    });
  }, [
    definition,
    cell,
    isEditMode,
    tableInstance,
    cellType,
    schema,
    cellClick,
    stopEdit,
    isValueVisible,
    autoFocus,
    selectionRange,
    onSelectionChange,
    getMaxSize,
    isRowVisible,
    rowInEditMode,
    isEditableCell,
    rowSizeMode,
    editableCols,
    onRowOut,
    row.original,
  ]);

  const cellStyle = useMemo(
    () => ({
      background: schemaValid ? undefined : 'var(--mui-palette-error-light)',
      ...style,
      padding: definition.type === 'picker' ? '0.125rem' : undefined,
      ...cellClassStyle,
    }),
    [schemaValid, style, definition.type, cellClassStyle]
  );

  const cellClassName = clsx(
    styles['table-cell'],
    (cell.column as ColumnAdditionalData).className,
    {
      [styles.skeleton]: showSkeleton,
      [styles.blurValue]: !isValueVisible,
      [styles.parent]: !isEditableCell,
      [styles['edit-mode']]:
        tableInstance.state.editModeConfig?.row === row.original.id,
      [styles.fitContent]: cell.column.id !== 'longtext',
    }
  );

  const cellProps = {
    ...otherCellProps,
    onClick: cellClick,
    className: cellClassName,
    style: cellStyle,
    id: cell.column.id,
    key: cell.column.id,
    kind: row.original.positionKind,
    shouldWrap: definition.type !== 'number' && definition.type !== 'picker',
    rowSizeMode,
    rowInEditMode,
    expandable:
      isEditableCell &&
      !cell.row.original.cacheOnly &&
      definition.isLongText &&
      definition.multiLinesRowKindsAllowed?.includes(
        cell.row.original.positionKind
      ),
    expanded,
    toggleExpanded,
  };

  return <TableCell {...cellProps}>{renderedCell}</TableCell>;
}

export const MemoizedCell = genericMemo(MemoizedCellInternal);

// Helper functions moved from the original file
function getTransform<T extends object>(
  definition: EditTableColumns<T>,
  schema: JsonSchema | null
) {
  if (definition.transform) return definition.transform;

  if (['date', 'time'].includes(definition.type)) {
    return (value: DateTime) => {
      return value?.toISO();
    };
  }
  if (definition.type === 'text' && schema) {
    if (schema.maxLength)
      return (text: string) => {
        if (!text) return text;
        const cuttedText = text.slice(0, schema.maxLength);
        return cuttedText;
      };
  }

  return definition.transform;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSchema<T extends object>(cell: Cell<T, any>) {
  if ('schema' in cell.column && cell.column.schema) {
    return cell.column.schema as JsonSchema;
  }
  return null;
}
